/**
 * Alerts — Config & Actions (cloud): what raises an alert, and who hears about it, for every site
 * and for the cloud itself. The same layout as a site's own page, site by site.
 *
 *   Left list               "Cloud (platform)" — the cloud server itself — then every site, with
 *                           who sends its notifications (Site / Cloud).
 *   Alert Config            a site: its AI rules and its CPU / memory / disk rules (both kept and
 *                           evaluated ON the site, edited here through its tunnel) and its
 *                           "site offline" alert (the cloud's — it is the one that notices).
 *                           Cloud (platform): the cloud's own CPU / memory / disk rules.
 *   Alert Actions           a site: Sent by (the SITE's one switch, changed through its tunnel),
 *                           the cloud's AI recipients and per-rule notifications, who hears about
 *                           its system and offline alerts, and "also email the site's users".
 *                           Cloud (platform): one list for what the cloud raises itself — sites going offline and the cloud server.
 *   Delivery channels       the cloud's Email (SMTP / Gmail API) and Message API Server.
 *   Delivery log            what the cloud sent — or, for one site, what that site sent itself.
 *
 * A site's alerts reach that site's people only: nothing here is added to every site's alerts.
 * Estate-wide and outside the scope system (constants/scope UNSCOPED_PATHS): it works in All
 * Sites, and opens on the switcher's site when one is chosen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CloudIcon,
  BuildingOffice2Icon,
  CpuChipIcon,
  EyeIcon,
  ServerIcon,
  ServerStackIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PageTitle,
  PageTabs,
  Callout,
  StatusPill,
  DeliveryChannelCard,
  RoutingPolicies,
  WatchlistOverview,
  EscalationsPanel,
  SilencesPanel,
  DeliveryReport,
  durationText,
  SentByCard,
  RecipientsCard,
  SystemRulesTable,
  DeliveryLogTable,
  SiteUsersCard,
  AASegmented,
  alertActionsErrorText,
  splitRecipients,
  type AlertActionRule,
  type RoutingPoliciesApi,
  type EscalationsApi,
  type SilencesApi,
  type EscalationMetrics,
  type AlertChannelKey,
  type AlertChannelState,
  type AlertChannelsApi,
  type DeliveryLogEntry,
  type HandledBy,
  type SiteUser,
  type SystemRule,
} from '@trinetra/shared';
import {
  getNodeAlertConfigs,
  getCentralAlertActions,
  saveCentralAlertChannel,
  testCentralAlertChannel,
  getCentralAlertLog,
  connectCentralGmail,
  saveCentralSiteAlertActions,
  testCentralSiteSystemAlert,
  getCentralSiteUsers,
  getCloudSystemRules,
  saveCloudSystemRules,
  getNodeSystemRules,
  saveNodeSystemRules,
  getNodeAlertLog,
  getNodeAlertActionsSite,
  saveNodeAlertActionsSite,
  testNodeSystemAlert,
  nodeRoutingApi,
  nodeEscalationsApi,
  nodeSilencesApi,
  getNodeDeliveryStats,
  getNodePersonsPage,
} from '../services/api.service';
import { listVehicleBlacklist } from '../services/vehicleBlacklist.service';
import { offlineRoutingApi } from '../services/api.service';
import OfflineTriggersCard from '../components/OfflineTriggersCard';
import { eventTypeLabel } from '../constants/alertRules';
import AnalyticsConfigPage from './AnalyticsConfigPage';

type Tab = 'config' | 'actions' | 'pause' | 'escalations' | 'channels' | 'log';
type Kind = 'ai' | 'system';
const TABS: Tab[] = ['config', 'actions', 'channels', 'pause', 'escalations', 'log'];
/** The left list's first entry: the cloud server itself. */
const PLATFORM = 'platform';
const CLOUD_SELF = 'cloud-self';

interface SiteRules {
  loading: boolean;
  error: string;
  rules: AlertActionRule[];
}

/** One site's settings as the CLOUD keeps them (cloud BE site_alert_actions). */
interface CentralSite {
  node_id: string;
  handled_by: HandledBy;
  ai: { email_recipients: string; message_api_recipients: string };
  system: { enabled: boolean; email_recipients: string; message_api_recipients: string };
  offline: { enabled: boolean; after_seconds: number };
  notify_site_users: boolean;
}

/** The site's OWN Alert Actions, read through its tunnel. */
interface SiteOwn {
  handled_by: HandledBy;
  system: { enabled: boolean; email_recipients: string; message_api_recipients: string };
  channels?: Record<string, { enabled: boolean; configured: boolean }>;
  cloud?: { available: boolean; reason: string | null };
}

interface Platform {
  enabled: boolean;
  email_recipients: string;
  message_api_recipients: string;
}

const channelsApi: AlertChannelsApi = {
  saveChannel: (c, body) => saveCentralAlertChannel(c, body),
  testChannel: (c, body) => testCentralAlertChannel(c, body),
  // Gmail API sender: the cloud's way round hosts that block outbound SMTP (DigitalOcean).
  gmailConnect: (redirectUri) => connectCentralGmail(redirectUri),
};

const emptyCentral = (nodeId: string): CentralSite => ({
  node_id: nodeId,
  handled_by: 'site',
  ai: { email_recipients: '', message_api_recipients: '' },
  system: { enabled: true, email_recipients: '', message_api_recipients: '' },
  offline: { enabled: true, after_seconds: 120 },
  notify_site_users: false,
});

/** A row of the cloud's delivery log (relayed_alert_notifications). */
function cloudLogEntry(row: any): DeliveryLogEntry {
  return {
    id: row.id,
    at: row.received_at,
    kind: row.kind || 'ai',
    site: row.node_id === CLOUD_SELF ? 'Cloud server' : row.node_name || row.node_id,
    title: row.alert_name || row.rule_name || eventTypeLabel(row.event_type),
    detail: [row.kind === 'ai' ? row.rule_name : null, row.severity].filter(Boolean).join(' · ') || null,
    source: row.camera_name,
    status: row.status,
    attempts: row.attempts,
    retry_at: row.next_try_at,
    requested: row.requested,
    results: row.results,
  };
}

/** A row of a site's own delivery log (its alert_delivery_log). */
function siteLogEntry(row: any): DeliveryLogEntry {
  return {
    id: row.id, at: row.created_at, kind: row.kind, title: row.title || 'Alert', detail: row.severity,
    source: row.source, via: row.via, status: row.status, requested: row.requested, results: row.results,
    attempts: row.attempts, retry_at: row.next_try_at, note: row.note, duration_ms: row.duration_ms,
  };
}

/**
 * Every site side by side (Escalations tab, "Cloud (platform)" selected): each online site's
 * last 7 days, read through its tunnel. A site that cannot answer says so in its row.
 */
function AllSitesEscalations({ nodes, api, onPick }: {
  nodes: { nodeId: string; name: string; status: string }[];
  api: (nodeId: string) => EscalationsApi;
  onPick: (nodeId: string) => void;
}) {
  const [rows, setRows] = useState<Record<string, { data?: EscalationMetrics; error?: string }>>({});
  const online = nodes.filter((n) => n.status === 'online');
  const key = online.map((n) => n.nodeId).join(',');
  useEffect(() => {
    let cancelled = false;
    setRows({});
    online.forEach((n) => {
      api(n.nodeId).metrics(7)
        .then((data) => { if (!cancelled) setRows((r) => ({ ...r, [n.nodeId]: { data } })); })
        .catch((err) => { if (!cancelled) setRows((r) => ({ ...r, [n.nodeId]: { error: alertActionsErrorText(err, 'No answer') } })); });
    });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');
  return (
    <div className="space-y-3">
      <Callout tone="info">Every site&apos;s last 7 days. Pick a site for its full Escalations view and its system alerts.</Callout>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border text-left text-[11px] text-muted-foreground">
              {['Site', 'Alerts', 'Got a response', 'Time to respond', 'Escalated', 'Not acked after every level', 'Escalating now', 'System alerts open'].map((h, i) => (
                <th key={h} className={`px-3 py-2 font-medium ${i ? 'text-right' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map((n) => {
              const st = rows[n.nodeId];
              const r = st?.data?.response;
              const cells = n.status !== 'online'
                ? ['Offline']
                : st?.error ? [st.error]
                  : !st ? ['Loading…']
                    : [
                      r ? r.total : '—', r ? pct(r.responded, r.total) : '—', r ? durationText(r.tta.avg) : '—',
                      r ? r.escalated : '—', r ? r.ended : '—', st.data!.open.length, st.data!.system.live,
                    ];
              return (
                <tr key={n.nodeId} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => onPick(n.nodeId)} className="font-medium text-brand hover:underline">{n.name}</button>
                  </td>
                  {cells.length === 1
                    ? <td colSpan={7} className="px-3 py-2 text-right text-muted-foreground">{cells[0]}</td>
                    : cells.map((c, i) => <td key={i} className="px-3 py-2 text-right tabular-nums text-foreground">{c}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CentralAlertActionsPage() {
  const { nodes, selectedNodeId: scopedSite } = useNode();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('alerts.config');

  const query = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState<Tab>(() => {
    const t = query.get('tab') || '';
    // Old links: ?tab=rules was the site list ("Sites").
    return t === 'rules' ? 'actions' : (TABS as string[]).includes(t) ? (t as Tab) : 'config';
  });
  const [kind, setKind] = useState<Kind>(() => (query.get('kind') === 'system' ? 'system' : 'ai'));
  // Alert Config's third view: the site's watchlist, read-only (changed on the Watch List page).
  const [configWatch, setConfigWatch] = useState(() => query.get('kind') === 'watchlist');
  const navigate = useNavigate();
  const [channels, setChannels] = useState<Record<AlertChannelKey, AlertChannelState> | null>(null);
  const [relayed24h, setRelayed24h] = useState<Record<string, { total: number; failed: number }>>({});
  const [overviewError, setOverviewError] = useState('');
  const [sites, setSites] = useState<Record<string, SiteRules>>({});
  const [central, setCentral] = useState<Record<string, CentralSite>>({});
  const [own, setOwn] = useState<Record<string, { value: SiteOwn | null; error: string }>>({});
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [cloudRules, setCloudRules] = useState<SystemRule[] | null>(null);
  const [siteRules, setSiteRules] = useState<Record<string, { rules: SystemRule[] | null; error: string }>>({});
  const [siteUsers, setSiteUsers] = useState<Record<string, SiteUser[]>>({});
  // Back from "Connect Google account" (the cloud's own email channel): open the cloud's channels.
  const fromGmail = useRef(!!(query.get('gmail') || query.get('gmail_error')));
  const [selected, setSelected] = useState<string | null>(() => query.get('site') || (fromGmail.current ? PLATFORM : scopedSite));
  const [log, setLog] = useState<DeliveryLogEntry[] | null>(null);
  const [logSource, setLogSource] = useState<'cloud' | 'site'>('cloud');
  const [logSite, setLogSite] = useState('');
  const [logKind, setLogKind] = useState('');
  const [logError, setLogError] = useState('');
  // Outcome of "Connect Google account" — Google redirects back with ?gmail=connected / ?gmail_error=.
  const [gmailNotice, setGmailNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(() => {
    if (query.get('gmail') === 'connected') {
      return { tone: 'success', text: 'Google account connected. Turn Email on below and send a test.' };
    }
    const err = query.get('gmail_error');
    return err ? { tone: 'danger', text: `Google account not connected: ${err}` } : null;
  });

  // Drop the one-shot ?gmail… parameters so a reload does not repeat the notice.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('gmail') || url.searchParams.has('gmail_error')) {
      url.searchParams.delete('gmail');
      url.searchParams.delete('gmail_error');
      window.history.replaceState(null, '', url);
    }
  }, []);

  const sortedNodes = useMemo(
    () => [...nodes].sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === 'online' ? -1 : 1)),
    [nodes],
  );

  const loadOverview = useCallback(async () => {
    try {
      const ov = await getCentralAlertActions();
      setChannels(ov.channels);
      setRelayed24h(Object.fromEntries((ov.relayed_24h || []).map((r: any) => [r.node_id, r])));
      setCentral(ov.sites || {});
      setPlatform(ov.platform || null);
      setOverviewError('');
    } catch (err) {
      setOverviewError(alertActionsErrorText(err, 'Failed to load the alert settings'));
    }
  }, []);

  const loadSite = useCallback(async (nodeId: string) => {
    setSites((s) => ({ ...s, [nodeId]: { rules: s[nodeId]?.rules || [], loading: true, error: '' } }));
    // The site's own switch and system recipients, alongside its rules. An older site
    // without the endpoint answers 404 — then the cloud's last report stands in.
    getNodeAlertActionsSite(nodeId)
      .then((v: SiteOwn) => setOwn((o) => ({ ...o, [nodeId]: { value: v, error: '' } })))
      .catch((err) => setOwn((o) => ({ ...o, [nodeId]: { value: null, error: alertActionsErrorText(err, 'Could not read this site’s Alert Actions') } })));
    try {
      const rules = await getNodeAlertConfigs(nodeId);
      setSites((s) => ({ ...s, [nodeId]: { rules, loading: false, error: '' } }));
    } catch (err) {
      setSites((s) => ({ ...s, [nodeId]: { rules: [], loading: false, error: alertActionsErrorText(err, 'Could not read this site’s rules') } }));
    }
  }, []);

  const loadSiteSystemRules = useCallback((nodeId: string) => {
    setSiteRules((r) => ({ ...r, [nodeId]: { rules: null, error: '' } }));
    getNodeSystemRules(nodeId)
      .then((rules: SystemRule[]) => setSiteRules((r) => ({ ...r, [nodeId]: { rules, error: '' } })))
      .catch((err) => setSiteRules((r) => ({
        ...r,
        [nodeId]: { rules: null, error: alertActionsErrorText(err, 'Could not read this site’s CPU / memory / disk rules — it may run an older version') },
      })));
  }, []);

  const loadSiteUsers = useCallback((nodeId: string) => {
    getCentralSiteUsers(nodeId).then((u: SiteUser[]) => setSiteUsers((s) => ({ ...s, [nodeId]: u }))).catch(() => {});
  }, []);

  const loadLog = useCallback(async () => {
    try {
      if (logSource === 'site' && selected && selected !== PLATFORM) {
        setLog((await getNodeAlertLog(selected, { kind: logKind || undefined, limit: 200 })).map(siteLogEntry));
      } else {
        const nodeId = logSite === PLATFORM ? CLOUD_SELF : logSite || undefined;
        setLog((await getCentralAlertLog({ nodeId, kind: (logKind || undefined) as any, limit: 200 })).map(cloudLogEntry));
      }
      setLogError('');
    } catch (err) {
      setLogError(alertActionsErrorText(err, 'Failed to load the delivery log'));
    }
  }, [logSource, logSite, logKind, selected]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Every online site's rules, once — the site list shows how many send email / messages.
  const onlineIds = sortedNodes.filter((n) => n.status === 'online').map((n) => n.nodeId).join(',');
  useEffect(() => {
    onlineIds.split(',').filter(Boolean).forEach((id) => {
      if (!sites[id]) loadSite(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineIds, loadSite]);

  useEffect(() => {
    if (!selected && sortedNodes.length) setSelected(sortedNodes[0].nodeId);
    if (!selected && !sortedNodes.length) setSelected(PLATFORM);
  }, [selected, sortedNodes]);

  // Follow the title-bar switcher: picking a site there opens it here.
  useEffect(() => {
    if (fromGmail.current) { fromGmail.current = false; return; }
    if (scopedSite) setSelected(scopedSite);
  }, [scopedSite]);

  // What the selected entry needs, loaded when first opened.
  useEffect(() => {
    if (!selected) return;
    const url = new URL(window.location.href);
    url.searchParams.set('site', selected);
    window.history.replaceState(null, '', url);
    if (selected === PLATFORM) {
      if (!cloudRules) getCloudSystemRules().then(setCloudRules).catch(() => setCloudRules([]));
      return;
    }
    if (!siteUsers[selected]) loadSiteUsers(selected);
    const online = sortedNodes.find((n) => n.nodeId === selected)?.status === 'online';
    if (online && !siteRules[selected] && tab === 'config' && kind === 'system') loadSiteSystemRules(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, tab, kind, sortedNodes]);

  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

  const setParam = (key: string, value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState(null, '', url);
  };
  const changeTab = (next: string) => { setTab(next as Tab); setParam('tab', next); };
  const changeKind = (next: Kind) => { setKind(next); setParam('kind', next); };
  const { selectNode } = useNode();

  const refresh = () => {
    loadOverview();
    if (selected && selected !== PLATFORM) {
      loadSite(selected);
      loadSiteUsers(selected);
      if (siteRules[selected]) loadSiteSystemRules(selected);
    }
    if (selected === PLATFORM) getCloudSystemRules().then(setCloudRules).catch(() => {});
    if (tab === 'log') loadLog();
  };

  // One routing / escalations API per site, kept stable so their panels do not reload on every render.
  const routingApis = useMemo(() => {
    const byNode = new Map<string, RoutingPoliciesApi>();
    return (nodeId: string) => {
      if (!byNode.has(nodeId)) byNode.set(nodeId, nodeRoutingApi(nodeId));
      return byNode.get(nodeId)!;
    };
  }, []);
  const escalationApis = useMemo(() => {
    const byNode = new Map<string, EscalationsApi>();
    return (nodeId: string) => {
      if (!byNode.has(nodeId)) byNode.set(nodeId, nodeEscalationsApi(nodeId));
      return byNode.get(nodeId)!;
    };
  }, []);
  const silenceApis = useMemo(() => {
    const byNode = new Map<string, SilencesApi>();
    return (nodeId: string) => {
      if (!byNode.has(nodeId)) byNode.set(nodeId, nodeSilencesApi(nodeId));
      return byNode.get(nodeId)!;
    };
  }, []);
  const deliveryStatsOf = useMemo(() => {
    const byNode = new Map<string, (days: number) => ReturnType<typeof getNodeDeliveryStats>>();
    return (nodeId: string) => {
      if (!byNode.has(nodeId)) byNode.set(nodeId, (days: number) => getNodeDeliveryStats(nodeId, days));
      return byNode.get(nodeId)!;
    };
  }, []);

  const isPlatform = selected === PLATFORM;
  // The cloud's Site offline cards (one api object: the screen keys its loads on it).
  const offlineApi = useMemo(() => offlineRoutingApi(), []);
  const site = selected && !isPlatform ? sortedNodes.find((n) => n.nodeId === selected) : undefined;
  const siteState = site ? sites[site.nodeId] : undefined;
  const siteOwn = site ? own[site.nodeId] : undefined;
  const siteCentral = site ? central[site.nodeId] || emptyCentral(site.nodeId) : undefined;
  /** Who sends a site's notifications: its own answer when reachable, else its last report here. */
  const handledByOf = (nodeId: string): HandledBy | null =>
    own[nodeId]?.value?.handled_by || central[nodeId]?.handled_by || null;
  const cloudSites = sortedNodes.filter((n) => handledByOf(n.nodeId) === 'cloud').length;
  const channelsOff = channels ? (['email', 'message_api'] as AlertChannelKey[]).filter((c) => !channels[c]?.enabled) : [];

  const saveOwn = async (nodeId: string, body: any) => {
    const v = await saveNodeAlertActionsSite(nodeId, body);
    setOwn((o) => ({ ...o, [nodeId]: { value: { ...(o[nodeId]?.value || {}), ...v }, error: '' } }));
    // The site reports a switch change to the cloud itself; mirror it now so the list agrees.
    if (body.handled_by) setCentral((c) => ({ ...c, [nodeId]: { ...(c[nodeId] || emptyCentral(nodeId)), handled_by: body.handled_by } }));
    return v;
  };
  const saveCentral = async (nodeId: string, body: any) => {
    const v = await saveCentralSiteAlertActions(nodeId, body);
    setCentral((c) => ({ ...c, [nodeId]: v }));
    return v;
  };

  const kindSwitch = (
    <AASegmented<Kind>
      value={kind}
      onChange={changeKind}
      options={[
        { value: 'ai', label: 'AI analytics alerts', icon: CpuChipIcon, hint: 'Alerts raised by the AI models — the site’s alert rules' },
        { value: 'system', label: 'System alerts', icon: ServerStackIcon, hint: 'A service failure, CPU / memory / disk too high, or the site going offline' },
      ]}
    />
  );

  // Alert Config: AI rules · Watchlist (read-only) · System rules.
  const configSwitch = (
    <AASegmented<Kind | 'watchlist'>
      value={configWatch ? 'watchlist' : kind}
      onChange={(next) => {
        if (next === 'watchlist') { setConfigWatch(true); setParam('kind', 'watchlist'); return; }
        setConfigWatch(false);
        changeKind(next);
      }}
      options={[
        { value: 'ai', label: 'AI analytics alerts', icon: CpuChipIcon, hint: 'Alerts raised by the AI models — the site’s alert rules' },
        { value: 'watchlist', label: 'Watchlist', icon: EyeIcon, hint: 'Listed people and vehicles — they raise an alert by themselves' },
        { value: 'system', label: 'System alerts', icon: ServerStackIcon, hint: 'A service failure, CPU / memory / disk too high, or the site going offline' },
      ]}
    />
  );
  const loadSiteWatchlist = useCallback(async (nodeId: string) => {
    const [people, vehicles] = await Promise.all([
      getNodePersonsPage(nodeId, { is_blacklisted: true, limit: 500 }),
      listVehicleBlacklist(nodeId, { limit: 500 }),
    ]);
    return { people: people.rows || [], vehicles: Array.isArray((vehicles as any)?.data) ? (vehicles as any).data : [] };
  }, []);

  const offlineNotice = site && site.status !== 'online' && (
    <Callout tone="info">
      This site is offline — what is kept on the site (its rules, who sends its notifications, its own recipients) can be changed
      once it reconnects.
      {handledByOf(site.nodeId) && <> It last reported that <span className="font-medium">{handledByOf(site.nodeId) === 'cloud' ? 'the cloud' : 'the site'}</span> sends them.</>}
      {' '}What the cloud keeps for it below can still be edited.
    </Callout>
  );

  // ─── Left list: the cloud, then every site ───
  const siteList = (
    <aside data-tour="alert-actions-sites" className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-3 py-2.5">
        <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Sites</h3>
        <p className="text-[11px] text-muted-foreground">{cloudSites} of {sortedNodes.length} sent by the cloud</p>
      </div>
      <ul className="max-h-[65vh] overflow-y-auto py-1">
        <li className="border-b border-border pb-1">
          <button
            type="button"
            onClick={() => setSelected(PLATFORM)}
            className={`flex w-full items-start gap-2 px-3 py-2 text-left ${isPlatform ? 'bg-brand-light' : 'hover:bg-muted'}`}
          >
            <ServerIcon className={`mt-0.5 h-4 w-4 shrink-0 ${isPlatform ? 'text-brand' : 'text-muted-foreground'}`} />
            <span className="min-w-0 flex-1">
              <span className={`block truncate text-[13px] text-foreground ${isPlatform ? 'font-medium' : ''}`}>Cloud (platform)</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                The cloud server itself
                {relayed24h[CLOUD_SELF] ? ` · ${relayed24h[CLOUD_SELF].total} sent 24 h` : ''}
              </span>
            </span>
          </button>
        </li>
        {sortedNodes.length === 0 ? (
          <li className="px-3 py-6 text-center text-[12px] text-muted-foreground">No sites registered yet.</li>
        ) : sortedNodes.map((n) => {
          const st = sites[n.nodeId];
          const by = handledByOf(n.nodeId);
          const recent = relayed24h[n.nodeId];
          const active = n.nodeId === selected;
          return (
            <li key={n.nodeId}>
              <button
                type="button"
                onClick={() => { setSelected(n.nodeId); if (!sites[n.nodeId] && n.status === 'online') loadSite(n.nodeId); }}
                className={`flex w-full items-start gap-2 px-3 py-2 text-left ${active ? 'bg-brand-light' : 'hover:bg-muted'}`}
              >
                {n.status === 'online'
                  ? <BuildingOffice2Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? 'text-brand' : 'text-muted-foreground'}`} />
                  : <SignalSlashIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className={`truncate text-[13px] text-foreground ${active ? 'font-medium' : ''}`}>{n.name}</span>
                    {by && (
                      <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded border border-border px-1 text-[10px] text-muted-foreground" title={by === 'cloud' ? 'Notifications sent by the cloud' : 'Notifications sent by the site'}>
                        {by === 'cloud' ? <CloudIcon className="h-3 w-3" /> : <BuildingOffice2Icon className="h-3 w-3" />}
                        {by === 'cloud' ? 'Cloud' : 'Site'}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {n.status !== 'online'
                      ? 'Offline'
                      : st?.loading && !st.rules.length ? 'Loading…'
                        : st?.error ? 'Rules unavailable'
                          : `${st?.rules.length ?? 0} rules`}
                    {recent ? ` · ${recent.total} sent 24 h${recent.failed ? ` (${recent.failed} failed)` : ''}` : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );

  const header = isPlatform ? (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">Cloud (platform)</h3>
      <StatusPill tone="neutral">The cloud server itself</StatusPill>
    </div>
  ) : site ? (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">{site.name}</h3>
      <StatusPill tone={site.status === 'online' ? 'success' : 'neutral'}>{site.status === 'online' ? 'Online' : 'Offline'}</StatusPill>
    </div>
  ) : null;

  // ─── Alert Config ───
  const configPanel = isPlatform ? (
    <>
    <SystemRulesTable
      title="The cloud's CPU, memory & disk"
      subtitle="Checked every 30 seconds on the cloud server. What fires appears on the System Alerts page; who is told is set on Who gets notified."
      rules={cloudRules}
      canEdit={canEdit}
      onSave={async (next) => {
        const stored = await saveCloudSystemRules(next);
        setCloudRules(stored);
        return stored;
      }}
    />
    <OfflineTriggersCard canEdit={canEdit} />
    </>
  ) : site ? (
    <>
      {configSwitch}
      {offlineNotice}
      {configWatch ? (
        site.status !== 'online' ? null : (
          <WatchlistOverview
            key={site.nodeId}
            load={() => loadSiteWatchlist(site.nodeId)}
            onManage={() => { selectNode(site.nodeId); navigate('/watch-list'); }}
          />
        )
      ) : kind === 'ai' ? (
        site.status !== 'online' ? null : <AnalyticsConfigPage key={site.nodeId} embedded siteId={site.nodeId} siteName={site.name} />
      ) : (
        <>
          <SystemRulesTable
            subtitle="Kept and checked on the site itself — every machine, every 30 seconds — so they work even when the site cannot reach the cloud. What fires is a system alert, sent as the Alert Actions tab says."
            rules={siteRules[site.nodeId]?.rules ?? null}
            error={siteRules[site.nodeId]?.error}
            unavailable={site.status !== 'online' ? 'The site is offline — its rules can be read and changed once it reconnects.' : undefined}
            canEdit={canEdit}
            onSave={async (next) => {
              const stored = await saveNodeSystemRules(site.nodeId, next);
              setSiteRules((r) => ({ ...r, [site.nodeId]: { rules: stored, error: '' } }));
              return stored;
            }}
          />
          <Callout tone="info">
            When this site counts as offline is set under <button type="button" onClick={() => setSelected(PLATFORM)} className="font-medium text-brand hover:underline">Cloud (platform)</button> → Alert Config → Site offline.
          </Callout>
        </>
      )}
    </>
  ) : null;

  // ─── Alert Actions ───
  // Cloud (platform): one list for what the cloud raises itself — sites going offline, the cloud server.
  const actionsPanel = isPlatform ? (
    <RoutingPolicies api={offlineApi} canEdit={canEdit} perspective="central" variant="offline" />
  ) : site ? (
    <>
      {offlineNotice}

      {site.status !== 'online' ? (
        <Callout tone="info">
          Who gets this site&apos;s alerts is set on the site itself (its routing policies), and can be read and changed here once it reconnects.
        </Callout>
      ) : (
        <RoutingPolicies
          key={site.nodeId}
          api={routingApis(site.nodeId)}
          canEdit={canEdit}
          perspective="central"
          eventTypeLabel={eventTypeLabel}
        />
      )}

      <Callout tone="info">
        Who hears that this site went offline is set under <button type="button" onClick={() => setSelected(PLATFORM)} className="font-medium text-brand hover:underline">Cloud (platform)</button> → Who gets notified.
      </Callout>

      <details className="rounded-xl border border-border bg-card px-4 py-3">
        <summary className="cursor-pointer text-[13px] font-medium text-foreground">Older site only: who hears its system alerts</summary>
        <div className="mt-3 space-y-3">
          <p className="text-[12px] text-muted-foreground">Used only by a site too old for its own Who gets notified — its service failures and CPU / memory / disk alerts go to these people.</p>
          <RecipientsCard
            icon={CloudIcon}
            title="System alerts of an older site"
            subtitle="Email addresses and WhatsApp numbers."
            canEdit={canEdit}
            value={siteCentral!.system}
            onSave={(v) => saveCentral(site.nodeId, { system: { email_recipients: v.email_recipients, message_api_recipients: v.message_api_recipients } })}
            onTest={() => testCentralSiteSystemAlert(site.nodeId, site.name)}
          />
          <SiteUsersCard
            value={siteCentral!.notify_site_users}
            users={siteUsers[site.nodeId] ?? null}
            canEdit={canEdit}
            onSave={(v) => saveCentral(site.nodeId, { notify_site_users: v })}
          />
        </div>
      </details>
    </>
  ) : null;

  // ─── Delivery channels ───
  // The cloud's own channels. They ALWAYS send the cloud's own alerts and every site's offline
  // alerts; a site's other alerts only while that site is "sent by the cloud".
  const cloudChannelCards = (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
      {(['email', 'message_api'] as AlertChannelKey[]).map((c) => (
        <DeliveryChannelCard
          key={c}
          channel={c}
          state={channels?.[c] || null}
          api={channelsApi}
          perspective="central"
          canEdit={canEdit && !!channels}
          onSaved={(next) => setChannels((prev) => (prev ? { ...prev, [c]: next } : prev))}
        />
      ))}
    </div>
  );
  const siteSentBy = site ? (siteOwn?.value?.handled_by || handledByOf(site.nodeId)) : null;
  const channelsPanel = isPlatform ? (
    <>
      {gmailNotice && <Callout tone={gmailNotice.tone}>{gmailNotice.text}</Callout>}
      <Callout tone="info">
        The cloud always sends its own alerts and every site&apos;s offline alerts with these channels — and the alerts of each site set to “sent by the cloud”.
      </Callout>
      {cloudChannelCards}
    </>
  ) : site ? (
    <>
      {offlineNotice}
      {site.status === 'online' && (siteOwn?.error && !siteOwn.value ? (
        <Callout tone="warning">
          {siteOwn.error}. This site may run an older version without site-wide Alert Actions — update it to choose who sends its notifications from here.
        </Callout>
      ) : (
        <SentByCard
          value={siteOwn?.value?.handled_by || null}
          canEdit={canEdit}
          perspective="central"
          onChange={(next) => saveOwn(site.nodeId, { handled_by: next })}
        />
      ))}
      {siteSentBy === 'cloud' ? (
        <>
          {gmailNotice && <Callout tone={gmailNotice.tone}>{gmailNotice.text}</Callout>}
          <Callout tone="info">The cloud sends this site&apos;s emails and messages with the cloud&apos;s channels below — the same channels for every site the cloud sends for.</Callout>
          {cloudChannelCards}
        </>
      ) : (
        <Callout tone="info">
          {site.name} sends its own emails and messages with its own delivery channels — they are set on the site. Only its offline alerts are sent by the cloud.
        </Callout>
      )}
    </>
  ) : null;

  // ─── Escalations: one site's metrics + system alerts, or every site side by side ───
  const escalationsPanel = isPlatform ? (
    <AllSitesEscalations nodes={sortedNodes} api={escalationApis} onPick={(id) => { setSelected(id); if (!sites[id]) loadSite(id); }} />
  ) : site ? (
    site.status !== 'online' ? (
      <Callout tone="info">This site is offline — its escalation metrics and system alerts are kept on the site and can be read once it reconnects.</Callout>
    ) : (
      <EscalationsPanel
        key={site.nodeId}
        api={escalationApis(site.nodeId)}
        kind={kind}
        canAct={hasPermission('alerts.edit')}
        header={kindSwitch}
        eventTypeLabel={eventTypeLabel}
      />
    )
  ) : null;

  // ─── Pause notifications: a site's pauses live on the site ───
  const pausePanel = isPlatform ? (
    <Callout tone="info">Pauses are kept per site, with its cameras and rules — pick a site on the left.</Callout>
  ) : site ? (
    site.status !== 'online' ? (
      <Callout tone="info">This site is offline — its pauses are kept on the site and can be read and changed once it reconnects.</Callout>
    ) : (
      <SilencesPanel key={site.nodeId} api={silenceApis(site.nodeId)} canEdit={canEdit} history={routingApis(site.nodeId).history} />
    )
  ) : null;

  const siteScoped = tab === 'config' || tab === 'actions' || tab === 'channels' || tab === 'pause' || tab === 'escalations';
  const selectedName = isPlatform ? 'the cloud' : site?.name;

  return (
    <div className="space-y-3">
      <PageTitle title="Alerts — Config & Actions" subtitle="What raises an alert on every site and on the cloud, and who hears about it">
        <button
          data-tour="alert-actions-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-muted"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </PageTitle>

      {overviewError && <Callout tone="danger">{overviewError}</Callout>}

      <PageTabs
        active={tab}
        onChange={changeTab}
        tabs={[
          { key: 'config', label: 'Alert Config' },
          { key: 'actions', label: 'Who gets notified' },
          { key: 'channels', label: 'Delivery channels' },
          { key: 'pause', label: 'Pause notifications' },
          { key: 'escalations', label: 'Escalations' },
          { key: 'log', label: 'Delivery log' },
        ]}
      />

      {siteScoped && (
        <div className="space-y-3">
          {tab === 'actions' && cloudSites > 0 && channelsOff.length === 2 && (
            <Callout tone="warning">
              The cloud sends for {cloudSites} site{cloudSites > 1 ? 's' : ''}, but both of the cloud&apos;s delivery channels are off.{' '}
              <button type="button" className="font-medium underline" onClick={() => changeTab('channels')}>Set up cloud delivery channels</button>
            </Callout>
          )}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
            {siteList}
            <section data-tour="alert-actions-rules" className="min-w-0 space-y-3">
              {!selected || (!isPlatform && !site) ? (
                <div className="rounded-xl border border-border bg-card px-4 py-10 text-center text-[13px] text-muted-foreground">Pick a site.</div>
              ) : (
                <>
                  {header}
                  {tab === 'config' ? configPanel : tab === 'channels' ? channelsPanel : tab === 'escalations' ? escalationsPanel : tab === 'pause' ? pausePanel : actionsPanel}
                </>
              )}
            </section>
          </div>
        </div>
      )}

      {tab === 'log' && logSource === 'site' && site && site.status === 'online' && (
        <DeliveryReport key={site.nodeId} load={deliveryStatsOf(site.nodeId)} />
      )}

      {tab === 'log' && (
        <div className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            {site && site.status === 'online' ? (
              <AASegmented<'cloud' | 'site'>
                value={logSource}
                onChange={setLogSource}
                options={[
                  { value: 'cloud', label: 'Sent by the cloud', icon: CloudIcon },
                  { value: 'site', label: `Sent by ${site.name}`, icon: BuildingOffice2Icon, hint: 'What this site sent itself, or handed to the cloud' },
                ]}
              />
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-foreground"><CloudIcon className="h-4 w-4 text-muted-foreground" />Sent by the cloud</span>
            )}
            <span className="flex-1" />
            <select
              value={logKind}
              onChange={(e) => setLogKind(e.target.value)}
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground"
            >
              <option value="">Every kind</option>
              <option value="ai">AI analytics alerts</option>
              <option value="system">System alerts</option>
              {logSource === 'cloud' && <option value="offline">Site offline</option>}
              {logSource === 'cloud' && <option value="cloud">Cloud</option>}
            </select>
            {logSource === 'cloud' && (
              <select
                value={logSite}
                onChange={(e) => setLogSite(e.target.value)}
                className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground"
              >
                <option value="">All sites + cloud</option>
                <option value={PLATFORM}>Cloud (platform)</option>
                {sortedNodes.map((n) => <option key={n.nodeId} value={n.nodeId}>{n.name}</option>)}
              </select>
            )}
          </div>
          <DeliveryLogTable
            entries={log}
            error={logError}
            showSite={logSource === 'cloud'}
            showVia={logSource === 'site'}
            emptyText={logSource === 'site' ? `Nothing sent by ${selectedName} yet.` : 'Nothing sent by the cloud yet.'}
          />
        </div>
      )}
    </div>
  );
}
